import { ReactElement, memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Input, message } from "antd";
import {
	X,
	FileText,
	MessageSquare,
	AlertTriangle,
	CheckCircle2,
	ShieldCheck,
	BookOpen,
	MapPin,
	ExternalLink,
	ChevronLeft,
	ChevronRight,
	Eye,
	CircleCheck,
	BadgeInfo,
	CircleAlert,
	TriangleAlert,
	ThumbsUp,
	Pencil,
	Send,
	Trash2
} from "lucide-react";

import Icon, { ICON_SOURCES, IconSource } from "components/icons/icon";
import ModalNavControls from "components/modal-nav-controls/modal-nav-controls";
import { InspectorFeedbackDecision, InspectorFeedbackErrorAttribution } from "constants/field-values";
import { useAddInspectorFeedbackMutation } from "graphql/mutations/add-inspector-feedback.generated";
import { useUpdateInspectorFeedbackMutation } from "graphql/mutations/update-inspector-feedback.generated";
import { useDeleteInspectorFeedbackMutation } from "graphql/mutations/delete-inspector-feedback.generated";
import { useLoggedUser } from "state/reactive-vars/logged-user-var";
import { toExternalUrl } from "utils/strings";

// Shared Apollo options for the three feedback mutations. errorPolicy "none" makes the
// promise reject on any GraphQL error, so each handler's try/catch is the single source
// of truth for success/failure messaging.
const FEEDBACK_MUTATION_OPTIONS = {
	errorPolicy: "none",
	fetchPolicy: "network-only",
	notifyOnNetworkStatusChange: true
} as const;

// ── Public types ────────────────────────────────────────────────────────────

/** One inspector_feedback row, exactly as it arrives nested under a finding or gap. */
export interface EvidenceModalInspectorFeedback {
	id: string;
	decision?: string | null;
	error_attribution?: string | null;
	comment?: string | null;
	user_id: string;
	created_at: string;
	updated_at: string;
}

export interface EvidenceModalSubmission {
	id: string;
	source_type?: string | null;
	file_name?: string | null;
	file_url?: string | null;
	file_size_bytes?: number | null;
	mime_type?: string | null;
	exif_gps_lat?: number | null;
	exif_gps_lon?: number | null;
	exif_timestamp?: string | null;
	review_status?: string | null;
	created_at?: string | null;
	location_verified?: boolean | null;
	location_verification?: Record<string, unknown> | null;
}

export interface EvidenceModalFinding {
	id: string;
	job_id: string;
	submission_id?: string | null;
	finding_status: string;
	description?: string | null;
	iris_analysis?: string | null;
	location?: string | null;
	confidence?: number | null;
	bbox_x?: number | null;
	bbox_y?: number | null;
	bbox_w?: number | null;
	bbox_h?: number | null;
	created_at?: string | null;
	inspector_feedbacks: EvidenceModalInspectorFeedback[];
}

export interface EvidenceModalCitation {
	id: string;
	tier?: string | null;
	source: string;
	section?: string | null;
	source_url?: string | null;
	page_numbers?: number[] | null;
}

export interface EvidenceModalGap {
	id: string;
	job_id: string;
	finding_id?: string | null;
	rule_id?: string | null;
	status: string;
	severity?: string | null;
	confidence?: number | null;
	reason: string;
	corrective_action?: string | null;
	verify_type?: string | null;
	requires_onsite_verification?: boolean | null;
	priority_rank?: number | null;
	created_at?: string | null;
	citations: EvidenceModalCitation[];
	inspector_feedbacks: EvidenceModalInspectorFeedback[];
}

export interface EvidenceModalItem {
	id: string;
	item_name: string;
	status?: string | null;
	required?: boolean | null;
	// jsonb: usually string[], but the schema is permissive — coerce defensively at render time.
	applicable_codes?: unknown;
	capture_instructions?: string | null;
	evaluation_criteria?: string | null;
	example_image_url?: string | null;
	doctrine_item_key?: string | null;
	doctrine_checklist_item_ref?: string | null;
	category?: string | null;
	item_category?: {
		label?: string | null;
		icon_source?: string | null;
		icon_name?: string | null;
	} | null;
	evidence_submissions: EvidenceModalSubmission[];
	evidence_findings: EvidenceModalFinding[];
	gap_analysis_items: EvidenceModalGap[];
}

export interface EvidenceModalProps {
	isOpen: boolean;
	onClose: () => void;
	item: EvidenceModalItem;
	/** Step to the previous/next checklist item without leaving the modal. Both are
	 *  optional: when the caller has no list to walk, only the close button renders. */
	onPrevious?: () => void;
	onNext?: () => void;
	hasPrevious?: boolean;
	hasNext?: boolean;
}

// ── Helpers ─────────────────────────────────────────────────────────────────

const mediaTypeFromMime = (mimeType?: string | null): "photo" | "video" | "document" => {
	if (!mimeType) return "document";
	if (mimeType.startsWith("image/")) return "photo";
	if (mimeType.startsWith("video/")) return "video";
	return "document";
};

const formatDate = (iso?: string | null): string => (iso ? new Date(iso).toLocaleString() : "—");

const humanize = (raw?: string | null): string =>
	raw
		? raw
				.replace(/_/g, " ")
				.toLowerCase()
				.replace(/\b\w/g, c => c.toUpperCase())
		: "";

// Gap status → pill visual
const gapStatusPillClass = (status?: string | null): ReactElement => {
	switch (status) {
		case "PASS":
			return (
				<span className={"gap-status"}>
					<div className={"gap-status-icon pass"} />
					{humanize(status)}
				</span>
			);
		case "FAIL":
			return (
				<span className={"gap-status"}>
					<div className={"gap-status-icon fail"} />
					{humanize(status)}
				</span>
			);
		case "WARNING":
		case "UNINSPECTED":
			return (
				<span className={"gap-status"}>
					<div className={"gap-status-icon warning"} />
					{humanize(status)}
				</span>
			);
		case "NOT_APPLICABLE":
			return (
				<span className={"gap-status"}>
					<div className={"gap-status-icon neutral"} />
					{humanize(status)}
				</span>
			);
		default:
			return (
				<span className={"gap-status"}>
					<div className={"gap-status-icon neutral"} />
					{humanize(status)}
				</span>
			);
	}
};

const gapCompactStatusPillClass = (status?: string | null): ReactElement | string => {
	switch (status) {
		case "PASS":
			return <span className={"em-pill-pass"}>{humanize(status)}</span>;
		case "FAIL":
			return <span className={"em-pill-fail"}>{humanize(status)}</span>;
		case "WARNING":
		case "UNINSPECTED":
			return <span className={"em-pill-warning"}>{humanize(status)}</span>;
		case "NOT_APPLICABLE":
			return <span className={"em-pill-neutral"}>{humanize(status)}</span>;
		default:
			return <span className={"em-pill-neutral"}>{humanize(status)}</span>;
	}
};

// Finding status → pill visual
const findingStatusPillClass = (status: string): ReactElement => {
	switch (status) {
		case "PRESENT":
			return (
				<span className={"em-pill-pass"}>
					<CircleCheck size={16} />
					{humanize(status)}
				</span>
			);
		case "MISSING":
		case "DAMAGED":
			return (
				<span className={"em-pill-fail"}>
					<TriangleAlert size={16} />
					{humanize(status)}
				</span>
			);
		case "UNCERTAIN":
		case "OBSTRUCTED":
			return (
				<span className={"em-pill-warning"}>
					<CircleAlert size={16} />
					{humanize(status)}
				</span>
			);
		default:
			return (
				<span className={"em-pill-neutral"}>
					<BadgeInfo size={16} />
					{humanize(status)}
				</span>
			);
	}
};

// Three-state inspector feedback. One rating + a disambiguation follow-up.
const DECISION_OPTIONS: { value: InspectorFeedbackDecision; label: string; icon: typeof CheckCircle2; cls: string }[] =
	[
		{
			value: InspectorFeedbackDecision.CONFIRMED,
			label: "Confirmed Accurate",
			icon: ThumbsUp,
			cls: "em-decision-confirmed"
		},
		{
			value: InspectorFeedbackDecision.PARTIALLY_CORRECT,
			label: "Partially Correct",
			icon: Pencil,
			cls: "em-decision-partial"
		},
		{ value: InspectorFeedbackDecision.INCORRECT, label: "Incorrect", icon: X, cls: "em-decision-incorrect" }
	];

// Disambiguation: maps the inspector's choice to error_attribution. PERCEPTION links the
// finding, REASONING links the gap item, BOTH links both (the parent resolves the ids).
const ATTRIBUTION_OPTIONS: { value: InspectorFeedbackErrorAttribution; label: string; icon: typeof CheckCircle2 }[] = [
	{ value: InspectorFeedbackErrorAttribution.PERCEPTION, label: "Observation was wrong", icon: Eye },
	{ value: InspectorFeedbackErrorAttribution.REASONING, label: "Compliance call was wrong", icon: ShieldCheck },
	{ value: InspectorFeedbackErrorAttribution.BOTH, label: "Both", icon: AlertTriangle }
];

const decisionLabel = (decision?: string | null): string | null =>
	decision ? (DECISION_OPTIONS.find(option => option.value === decision)?.label ?? humanize(decision)) : null;

const attributionLabel = (attribution?: string | null): string | null =>
	attribution
		? (ATTRIBUTION_OPTIONS.find(option => option.value === attribution)?.label ?? humanize(attribution))
		: null;

const decisionTagClass = (decision?: string | null): string => {
	if (decision === InspectorFeedbackDecision.CONFIRMED) return "em-feedback-history-tag-up";
	if (decision === InspectorFeedbackDecision.PARTIALLY_CORRECT) return "em-feedback-history-tag-warn";
	return "em-feedback-history-tag-down";
};

// Icon + colour for the saved-feedback attribution chip — mirrors the attribution option
// buttons. One subsystem off (perception OR reasoning) = warning amber; both off = fail red.
const attributionVisual = (attribution?: string | null): { icon: typeof CheckCircle2; cls: string } => {
	switch (attribution) {
		case InspectorFeedbackErrorAttribution.PERCEPTION:
			return { icon: Eye, cls: "em-feedback-history-attr-warning" };
		case InspectorFeedbackErrorAttribution.REASONING:
			return { icon: ShieldCheck, cls: "em-feedback-history-attr-warning" };
		case InspectorFeedbackErrorAttribution.BOTH:
			return { icon: AlertTriangle, cls: "em-feedback-history-attr-fail" };
		default:
			return { icon: CheckCircle2, cls: "em-feedback-history-attr-pass" };
	}
};

// Coarse confidence level + how many of the 3 bars to fill (accepts 0–1 or 0–100).
const confidenceLevel = (confidence?: number | null): { label: string; bars: number } | null => {
	if (confidence == null) return null;
	const ratio = confidence <= 1 ? confidence : confidence / 100;
	if (ratio >= 0.8) return { label: "High", bars: 3 };
	if (ratio >= 0.5) return { label: "Medium", bars: 2 };
	return { label: "Low", bars: 1 };
};

const ConfidenceMeter = ({ value }: { value?: number | null }) => {
	const level = confidenceLevel(value);
	if (!level) return null;
	return (
		<span className="em-an-confidence">
			<span className="em-an-conf-label">Confidence</span>
			<span className="em-an-conf-bars">
				{[1, 2, 3].map(barIndex => (
					<span key={barIndex} className={`em-an-conf-bar ${barIndex <= level.bars ? "em-an-conf-bar-on" : ""}`} />
				))}
			</span>
			<span className="em-an-conf-level">{level.label}</span>
		</span>
	);
};

// ── Component ───────────────────────────────────────────────────────────────

const EvidenceModal = ({ isOpen, onClose, item, onPrevious, onNext, hasPrevious, hasNext }: EvidenceModalProps) => {
	const { loggedUserValue } = useLoggedUser();
	const currentUserId = loggedUserValue?.id;

	const [decision, setDecision] = useState<InspectorFeedbackDecision | null>(null);
	const [errorAttribution, setErrorAttribution] = useState<InspectorFeedbackErrorAttribution | null>(null);
	const [comment, setComment] = useState("");
	const [submitError, setSubmitError] = useState<string | null>(null);
	const [isEditing, setIsEditing] = useState(false);
	// True only after a successful add/edit in THIS modal session — drives the ephemeral
	// "Feedback received" banner. Reset when the modal closes or switches item, so it never
	// shows just because saved feedback already exists.
	const [justSubmitted, setJustSubmitted] = useState(false);
	const [currentSubmissionIndex, setCurrentSubmissionIndex] = useState(0);
	const modalRef = useRef<HTMLDivElement>(null);

	const [addInspectorFeedbackMutation, { loading: adding }] = useAddInspectorFeedbackMutation({
		...FEEDBACK_MUTATION_OPTIONS
	});
	const [updateInspectorFeedbackMutation, { loading: updating }] = useUpdateInspectorFeedbackMutation({
		...FEEDBACK_MUTATION_OPTIONS
	});
	const [deleteInspectorFeedbackMutation, { loading: deleting }] = useDeleteInspectorFeedbackMutation({
		...FEEDBACK_MUTATION_OPTIONS
	});
	const submitting = adding || updating;

	// Clears the feedback form back to its empty/non-editing state.
	const resetFeedbackForm = useCallback(() => {
		setDecision(null);
		setErrorAttribution(null);
		setComment("");
		setSubmitError(null);
		setIsEditing(false);
	}, []);

	// Escape closes, and the page behind must not scroll while the modal is up.
	useEffect(() => {
		if (!isOpen) return;
		const onKeyDown = (event: KeyboardEvent) => {
			if (event.key === "Escape") onClose();
		};
		window.addEventListener("keydown", onKeyDown);
		const previousOverflow = window.document.body.style.overflow;
		window.document.body.style.overflow = "hidden";
		return () => {
			window.removeEventListener("keydown", onKeyDown);
			window.document.body.style.overflow = previousOverflow;
		};
	}, [isOpen, onClose]);

	// Discard any in-progress feedback and the confirmation banner when the modal closes.
	useEffect(() => {
		if (!isOpen) {
			resetFeedbackForm();
			setJustSubmitted(false);
		}
	}, [isOpen, resetFeedbackForm]);

	// Reset carousel position, any in-progress edit, and the banner when the modal switches
	// item. Stepping with Previous/Next keeps the modal mounted, so the scroll position has
	// to be sent back to the top by hand or the new item opens part-way down.
	useEffect(() => {
		setCurrentSubmissionIndex(0);
		resetFeedbackForm();
		setJustSubmitted(false);
		modalRef.current?.scrollTo({ top: 0 });
	}, [item.id, resetFeedbackForm]);

	useEffect(() => {
		if (currentSubmissionIndex >= item.evidence_submissions.length && item.evidence_submissions.length > 0) {
			setCurrentSubmissionIndex(item.evidence_submissions.length - 1);
		}
	}, [item.evidence_submissions.length, currentSubmissionIndex]);

	// Resolves, from `item`, everything the feedback panel needs:
	//   • reviewedGap / reviewedFinding — the gap + finding the inspector is judging (report
	//     items are 1:1, so we take the first gap and the finding it was derived from).
	//   • savedFeedbacks — the inspector_feedback rows already in the DB for those two rows,
	//     de-duped (one row can be linked to both) and ordered newest-first.
	//   • findingId / gapItemId / jobId — the foreign keys a new/edited feedback attaches to.
	// Memoised on `item` so typing in the comment box doesn't re-collect/re-sort each keystroke.
	const feedbackData = useMemo(() => {
		const reviewedGap = item.gap_analysis_items[0];
		const reviewedFinding = reviewedGap?.finding_id
			? item.evidence_findings.find(finding => finding.id === reviewedGap.finding_id)
			: undefined;

		// A single feedback row can be linked to both the gap and the finding, so de-dupe by id.
		const feedbacksById = new Map<string, EvidenceModalInspectorFeedback>();
		[...(reviewedGap?.inspector_feedbacks ?? []), ...(reviewedFinding?.inspector_feedbacks ?? [])].forEach(
			feedbackRow => feedbacksById.set(feedbackRow.id, feedbackRow)
		);
		const savedFeedbacks = Array.from(feedbacksById.values()).sort(
			(a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
		);

		return {
			savedFeedbacks,
			// Fall back to the item's first finding so a new feedback always has a row to land on.
			findingId: reviewedFinding?.id ?? item.evidence_findings[0]?.id ?? null,
			gapItemId: reviewedGap?.id ?? null,
			jobId: reviewedGap?.job_id ?? item.evidence_findings[0]?.job_id ?? null
		};
	}, [item]);

	if (!isOpen) return null;

	// For now we surface a single feedback per item regardless of author: the newest saved row.
	// It can be edited or deleted in place. Feedback UI shows only when there's a row to attach to.
	const latestFeedback = feedbackData.savedFeedbacks[0] ?? null;
	const canGiveFeedback = !!currentUserId && (feedbackData.findingId !== null || feedbackData.gapItemId !== null);

	// Re-open the form pre-filled with the saved feedback so the inspector can amend it.
	const handleStartEdit = () => {
		if (!latestFeedback) return;
		setDecision((latestFeedback.decision as InspectorFeedbackDecision) ?? null);
		setErrorAttribution((latestFeedback.error_attribution as InspectorFeedbackErrorAttribution) ?? null);
		setComment(latestFeedback.comment ?? "");
		setSubmitError(null);
		setIsEditing(true);
	};

	const handleSubmitFeedback = async () => {
		if (!currentUserId || decision === null) return;
		const needsAttribution = decision !== InspectorFeedbackDecision.CONFIRMED;
		// On the non-confirming paths, the inspector must either pick which part was off
		// or explain in a comment (the "can't pick a clean attribution" escape hatch).
		if (needsAttribution && errorAttribution === null && comment.trim().length === 0) {
			setSubmitError("Tell Ollie which part was off, or add a short comment.");
			return;
		}
		setSubmitError(null);

		const attribution = needsAttribution ? errorAttribution : null;

		// `feedbackData` already resolved the base finding/gap ids (with the [0] fallback).
		// The attribution choice then narrows it: PERCEPTION → finding, REASONING → gap.
		let findingId: string | null = feedbackData.findingId;
		let gapItemId: string | null = feedbackData.gapItemId;
		if (attribution === InspectorFeedbackErrorAttribution.PERCEPTION) {
			gapItemId = null;
		} else if (attribution === InspectorFeedbackErrorAttribution.REASONING) {
			findingId = null;
		}

		if ((!findingId && !gapItemId) || !feedbackData.jobId) {
			message.error("Couldn't attach this feedback — no finding or compliance item is available.");
			return;
		}

		try {
			if (isEditing && latestFeedback) {
				// Editing: update the existing row in place rather than inserting a duplicate.
				// user_id is rewritten to the current inspector so they become the row's owner.
				await updateInspectorFeedbackMutation({
					variables: {
						id: latestFeedback.id,
						_set: {
							user_id: currentUserId,
							decision,
							error_attribution: attribution,
							comment: comment.trim(),
							updated_at: new Date().toISOString()
						}
					}
				});
				message.success("Your feedback was updated.");
			} else {
				await addInspectorFeedbackMutation({
					variables: {
						input: {
							job_id: feedbackData.jobId,
							user_id: currentUserId,
							decision,
							error_attribution: attribution,
							comment: comment.trim(),
							finding_id: findingId,
							gap_item_id: gapItemId
						}
					}
				});
				message.success("Thanks — your feedback was recorded.");
			}
			resetFeedbackForm();
			// Show the ephemeral confirmation banner for the rest of this modal session.
			setJustSubmitted(true);
		} catch (error) {
			console.error("Failed to record feedback:", error);
			message.error("Couldn't save your feedback. Please try again.");
		}
	};

	const handleDeleteFeedback = async () => {
		if (!latestFeedback) return;
		try {
			await deleteInspectorFeedbackMutation({ variables: { id: latestFeedback.id } });
			message.success("Your feedback was removed.");
			resetFeedbackForm();
		} catch (error) {
			console.error("Failed to remove feedback:", error);
			message.error("Couldn't remove your feedback. Please try again.");
		}
	};

	const categoryLabel = item.item_category?.label ?? humanize(item.category);
	const iconSource = (item.item_category?.icon_source as IconSource) ?? ICON_SOURCES.LUCIDE;
	const iconName = item.item_category?.icon_name ?? "ShieldCheck";

	return (
		<div className="em-overlay">
			<div className="em-backdrop" onClick={onClose} />

			<div className="em-modal" ref={modalRef}>
				{/* ── Header ─────────────────────────────────────────────────── */}
				<div className="em-modal-header">
					<div className="em-modal-title-row">
						<div className="em-type-icon-wrap">
							<Icon source={iconSource} name={iconName} size={20} />
						</div>

						{(categoryLabel || item.item_name) && (
							<span className="em-modal-header-item-category-name">{`${categoryLabel ? categoryLabel + " - " : ""}${item.item_name}`}</span>
						)}
					</div>
					<ModalNavControls
						onPrevious={onPrevious}
						onNext={onNext}
						hasPrevious={hasPrevious}
						hasNext={hasNext}
						onClose={onClose}
					/>
				</div>

				{/* ── Submitted evidence ─────────────────────────────────────── */}
				<section className="em-section">
					{item.evidence_submissions.length === 0 ? (
						<p className="em-section-empty">No evidence has been submitted for this item yet.</p>
					) : (
						(() => {
							const total = item.evidence_submissions.length;
							const activeIndex = Math.min(currentSubmissionIndex, total - 1);
							const currentSubmission = item.evidence_submissions[activeIndex];
							const mediaType = mediaTypeFromMime(currentSubmission.mime_type);
							const showCarouselControls = total > 1;
							const goPrev = () => setCurrentSubmissionIndex(prev => (prev - 1 + total) % total);
							const goNext = () => setCurrentSubmissionIndex(prev => (prev + 1) % total);

							// Swipe (touch + mouse) via Pointer Events. We capture the pointer on press
							// so every subsequent event (including the release) is guaranteed to land on
							// this container — even if the gesture started on the image/video child.
							let swipeStartX: number | null = null;
							const SWIPE_THRESHOLD = 50;
							const handlePointerDown = (e: React.PointerEvent) => {
								if (total <= 1) return;
								// Don't hijack gestures that start on the nav buttons, dots or links —
								// capturing the pointer there would swallow their click.
								if ((e.target as HTMLElement).closest("button, a")) return;
								swipeStartX = e.clientX;
								e.currentTarget.setPointerCapture(e.pointerId);
							};
							const handlePointerUp = (e: React.PointerEvent) => {
								if (swipeStartX === null) return;
								const dx = e.clientX - swipeStartX;
								swipeStartX = null;
								if (Math.abs(dx) < SWIPE_THRESHOLD) return;
								if (dx < 0) goNext();
								else goPrev();
							};
							return (
								<div
									className="em-submission-carousel"
									onPointerDown={handlePointerDown}
									onPointerUp={handlePointerUp}
									onPointerCancel={() => {
										swipeStartX = null;
									}}
									onDragStart={e => e.preventDefault()}
									style={{ touchAction: "pan-y" }}
								>
									{showCarouselControls && (
										<div className="em-carousel-header">
											<button
												type="button"
												className="em-carousel-nav-btn"
												onClick={goPrev}
												aria-label="Previous evidence"
											>
												<ChevronLeft className="em-carousel-nav-icon" />
											</button>
											<span className="em-carousel-counter">
												{activeIndex + 1} / {total}
											</span>
											<button type="button" className="em-carousel-nav-btn" onClick={goNext} aria-label="Next evidence">
												<ChevronRight className="em-carousel-nav-icon" />
											</button>
										</div>
									)}

									<div key={currentSubmission.id} className="em-submission-card">
										{mediaType === "photo" && currentSubmission.file_url && (
											<div className="em-media-wrap">
												<img
													src={currentSubmission.file_url}
													alt={currentSubmission.file_name ?? "Evidence"}
													className="em-media-img"
												/>
											</div>
										)}
										{mediaType === "video" && currentSubmission.file_url && (
											<div className="em-media-wrap em-media-wrap-video">
												<video src={currentSubmission.file_url} className="em-media-img" controls preload="metadata" />
											</div>
										)}
										{mediaType === "document" && (
											<div className="em-media-placeholder em-media-placeholder-doc">
												<FileText className="em-placeholder-icon" />
												<p className="em-placeholder-label">{currentSubmission.file_name ?? "Document"}</p>
												{currentSubmission.mime_type && (
													<p className="em-placeholder-sub">{currentSubmission.mime_type}</p>
												)}
												{currentSubmission.file_url && (
													<a className="em-doc-link" href={currentSubmission.file_url} target="_blank" rel="noreferrer">
														Open document
													</a>
												)}
											</div>
										)}
										{currentSubmission.location_verified === false && (
											<div className="em-location-unverified">
												<AlertTriangle className="em-location-unverified-icon" />
												<span>
													Location not verified — GPS signal was insufficient to confirm this photo was captured
													on-site.
												</span>
											</div>
										)}
									</div>

									{showCarouselControls && (
										<div className="em-carousel-dots" role="tablist" aria-label="Evidence files">
											{item.evidence_submissions.map((submission, dotIndex) => (
												<button
													key={submission.id}
													type="button"
													role="tab"
													aria-selected={dotIndex === activeIndex}
													aria-label={`Show evidence ${dotIndex + 1}`}
													className={`em-carousel-dot ${dotIndex === activeIndex ? "em-carousel-dot-active" : ""}`}
													onClick={() => setCurrentSubmissionIndex(dotIndex)}
												/>
											))}
										</div>
									)}
								</div>
							);
						})()
					)}
				</section>

				{/* ── Analysis: Observations (perception) + Compliance (reasoning) ──
				     Single labeled block, mirroring the collection-time design. Renders the
				     primary finding + gap (report items are 1:1). Citation lives in Compliance. */}
				{(item.evidence_findings.length > 0 || item.gap_analysis_items.length > 0) &&
					(() => {
						const finding = item.evidence_findings[0];
						const gap = item.gap_analysis_items[0];
						return (
							<section className="em-analysis">
								<div className="em-analysis-head">
									<div className="em-analysis-avatar">
										<img src={`${process.env.PUBLIC_URL}/assets/images/icons/ollie-owl.svg`} alt="Ollie AI" />
									</div>
									<div>
										<p className="em-analysis-title">Ollie&apos;s Analysis</p>
										<p className="em-analysis-subtitle">Automated compliance review</p>
									</div>
								</div>

								{/* Observations — what Ollie perceived */}
								{finding && (
									<div className="em-an-card">
										<div className="em-an-section-head">
											<span className="em-an-section-label">
												<Eye className="em-an-section-icon" />
												Observations
											</span>
											<div className="em-an-meta">
												<ConfidenceMeter value={finding.confidence} />
												{finding.finding_status && (
													<>
														<span className="em-an-divider" />
														{findingStatusPillClass(finding.finding_status)}
													</>
												)}
											</div>
										</div>
										{finding.iris_analysis && <p className="em-an-text">{finding.iris_analysis}</p>}
									</div>
								)}

								{/* Compliance — code judgment (citation belongs here) */}
								{gap && (
									<div className="em-an-card">
										<div className="em-an-section-head">
											<span className="em-an-section-label">
												<ShieldCheck className="em-an-section-icon" />
												Compliance
											</span>
											<div className="em-an-meta">
												<ConfidenceMeter value={gap.confidence} />
												<span className="em-an-divider" />
												{gapStatusPillClass(gap.status)}
											</div>
										</div>

										<div className="em-an-verdict">
											<div className="em-an-verdict-row">
												{gapCompactStatusPillClass(gap.status)}
												{gap.severity && (
													<span className="gap-severity">{`• ${humanize(gap.severity)} Severity${gap.requires_onsite_verification ? " • " : ""}`}</span>
												)}
												{gap.requires_onsite_verification && (
													<span className="em-pill-neutral">
														<MapPin className="em-pill-icon" />
														On-site Required
													</span>
												)}
											</div>
											{gap.reason && <p className="em-an-text">{gap.reason}</p>}
											{gap.corrective_action && gap.corrective_action !== "null" && (
												<div className="em-an-action">
													<CircleAlert className="em-an-action-icon" />
													<div>
														<p className="em-an-action-label">Recommended Action</p>
														<p className="em-an-action-text">{gap.corrective_action}</p>
													</div>
												</div>
											)}
										</div>

										{(gap.citations.length > 0 || gap.rule_id) && (
											<div className="em-an-reference">
												<span className="em-an-reference-label">Reference Source</span>
												{gap.citations.length > 0
													? gap.citations.map(citation => {
															const firstPage = citation.page_numbers?.[0];
															const sourceUrl = toExternalUrl(citation.source_url);
															const isPdf = !!sourceUrl?.toLowerCase().includes(".pdf");
															const href = sourceUrl
																? firstPage && isPdf
																	? `${sourceUrl}#page=${firstPage}`
																	: sourceUrl
																: null;
															return (
																<span key={citation.id} className="em-an-reference-chip">
																	<span className="em-an-reference-code">
																		{citation.source}
																		{citation.section ? ` § ${citation.section}` : ""}
																	</span>
																	{citation.tier && (
																		<>
																			<span className="em-an-reference-sep" />
																			<BookOpen className="em-an-reference-ai-icon" />
																			<span className="em-an-reference-ai">{humanize(citation.tier)}</span>
																		</>
																	)}
																	{href && (
																		<a
																			className="em-an-reference-link"
																			href={href}
																			target="_blank"
																			rel="noreferrer"
																			aria-label="Open citation source"
																		>
																			<ExternalLink className="em-meta-inline-icon" />
																			{firstPage && isPdf ? `Open (p. ${firstPage})` : "Open"}
																		</a>
																	)}
																</span>
															);
														})
													: gap.rule_id && (
															<span className="em-an-reference-chip">
																<span className="em-an-reference-code em-mono">{gap.rule_id}</span>
															</span>
														)}
											</div>
										)}
									</div>
								)}
							</section>
						);
					})()}

				{/* ── Inspector feedback (three-state + perception/reasoning disambiguation) ── */}
				{canGiveFeedback && (
					<div className="em-feedback-section">
						<div className="em-feedback-header">
							<div className="em-feedback-header-icon-container">
								<MessageSquare className="em-feedback-header-icon" />
							</div>
							<div>
								<span className="em-feedback-header-title">Inspector Feedback</span>
								<p className="em-feedback-header-subtitle">Is this AI analysis accurate?</p>
							</div>
						</div>

						{latestFeedback && !isEditing ? (
							/* ── Saved feedback: green confirmation + edit / delete ─────────────── */
							<div className="em-feedback-received">
								{/* Ephemeral: shown only right after a successful add/edit; gone on reopen. */}
								{justSubmitted && (
									<div className="em-feedback-received-banner">
										<CheckCircle2 className="em-feedback-received-icon" />
										<div>
											<p className="em-feedback-received-title">Feedback received</p>
											<p className="em-feedback-received-subtitle">Thank you for helping Ollie improve!</p>
										</div>
									</div>
								)}

								{(() => {
									const d = latestFeedback.decision ?? null;
									const attribution = latestFeedback.error_attribution ?? null;
									const attr = attributionLabel(attribution);
									const attrVisual = attributionVisual(attribution);
									const AttrIcon = attrVisual.icon;
									return (
										<div className="em-feedback-saved">
											<div className="em-feedback-history-head">
												<span className={`em-feedback-history-tag ${decisionTagClass(d)}`}>
													{decisionLabel(d) ?? "Feedback"}
												</span>
												{attr && (
													<span className={`em-feedback-history-attr ${attrVisual.cls}`}>
														<AttrIcon className="em-feedback-history-attr-icon" />
														{attr}
													</span>
												)}
												{latestFeedback.updated_at && <p>{formatDate(latestFeedback.updated_at)}</p>}
											</div>
											{latestFeedback.comment && (
												<p className="em-feedback-history-comment">{latestFeedback.comment}</p>
											)}
										</div>
									);
								})()}

								<div className="em-feedback-submit-row">
									<button className="em-feedback-cancel-button" onClick={handleStartEdit}>
										<Pencil size={14} /> Edit
									</button>
									<button className="em-feedback-delete-button" onClick={handleDeleteFeedback} disabled={deleting}>
										<Trash2 size={14} /> {deleting ? "Deleting…" : "Delete"}
									</button>
								</div>
							</div>
						) : (
							/* ── Feedback form (create or edit) ─────────────────────────────────── */
							<>
								<div className="em-decision-row">
									{DECISION_OPTIONS.map(option => {
										const DecisionIcon = option.icon;
										const isActive = decision === option.value;
										return (
											<button
												key={option.value}
												type="button"
												className={`em-decision-btn ${option.cls} ${isActive ? "em-decision-btn-active" : ""}`}
												onClick={() => {
													setDecision(option.value);
													if (option.value === InspectorFeedbackDecision.CONFIRMED) setErrorAttribution(null);
													setSubmitError(null);
												}}
											>
												<DecisionIcon className="em-decision-btn-icon" />
												{option.label}
											</button>
										);
									})}
								</div>

								{/* Disambiguation + comment — only on the two non-confirming paths. */}
								{decision && decision !== InspectorFeedbackDecision.CONFIRMED && (
									<>
										<div className="em-attribution">
											<p className="em-attribution-label">Which part was off?</p>
											<div className="em-attribution-options">
												{ATTRIBUTION_OPTIONS.map(option => {
													const AttributionIcon = option.icon;
													const isActive = errorAttribution === option.value;
													return (
														<button
															key={option.value}
															type="button"
															className={`em-attribution-btn ${isActive ? "em-attribution-btn-active" : ""}`}
															onClick={() => {
																setErrorAttribution(isActive ? null : option.value);
																setSubmitError(null);
															}}
														>
															<AttributionIcon className="em-attribution-btn-icon" />
															{option.label}
														</button>
													);
												})}
											</div>
										</div>

										<p className="em-attribution-label">
											{decision === InspectorFeedbackDecision.INCORRECT
												? "Reason for incorrect analysis (Optional)"
												: "What's missing or slightly off? (Optional)"}
										</p>
										<Input.TextArea
											className="em-feedback-comment"
											rows={3}
											placeholder={"Please explain why the AI analysis is incorrect to help us improve..."}
											value={comment}
											onChange={e => setComment(e.target.value)}
										/>
									</>
								)}

								{decision && (
									<>
										{submitError && <p className="em-feedback-error">{submitError}</p>}
										<div className="em-feedback-submit-row">
											<button className="em-feedback-cancel-button" onClick={resetFeedbackForm}>
												Cancel
											</button>
											<button
												className="em-feedback-submit-button"
												onClick={handleSubmitFeedback}
												disabled={submitting}
											>
												<Send size={16} color="white" strokeWidth={1.33} />{" "}
												{submitting ? "Saving…" : isEditing ? "Save changes" : "Submit feedback"}
											</button>
										</div>
									</>
								)}
							</>
						)}
					</div>
				)}

				{/* ── Capture metadata ──────────────────────────────────────── */}
				{(() => {
					const primary = item.evidence_submissions[0];
					const capturedAt = primary?.exif_timestamp ?? primary?.created_at;
					const location = item.evidence_findings[0]?.location ?? item.item_name;
					if (!capturedAt && !location) return null;
					return (
						<div className="em-capture-meta">
							{capturedAt && (
								<p className="em-capture-meta-row">
									<strong>Captured:</strong> {formatDate(capturedAt)}
								</p>
							)}
							{location && location !== "null" && (
								<p className="em-capture-meta-row">
									<strong>Location:</strong> {location}
								</p>
							)}
						</div>
					);
				})()}
			</div>
		</div>
	);
};

export default memo(EvidenceModal);
